import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import PartnerCard from './components/PartnerCard';
import PartnerFormModal from './components/PartnerFormModal';
import { partnerService, type Partner } from '../../../services/partner.service';

export default function PartnersPage() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPartner, setEditPartner] = useState<Partner | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [filterVisible, setFilterVisible] = useState<'all' | 'visible' | 'hidden'>('all');

  const loadPartners = () => {
    setLoading(true);
    partnerService.list().then((data) => {
      setPartners(data.sort((a, b) => a.order - b.order));
      setLoading(false);
    });
  };

  useEffect(() => { loadPartners(); }, []);

  const filtered = partners.filter((p) => {
    if (filterVisible === 'visible') return p.visible;
    if (filterVisible === 'hidden') return !p.visible;
    return true;
  });

  const moveItem = async (id: number, direction: 'up' | 'down') => {
    const idx = partners.findIndex((p) => p.id === id);
    if (direction === 'up' && idx <= 0) return;
    if (direction === 'down' && idx >= partners.length - 1) return;
    const next = [...partners];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    const reordered = next.map((p, i) => ({ ...p, order: i + 1 }));
    setPartners(reordered);
    await partnerService.reorder(reordered.map((p) => p.id));
  };

  const handleToggleVisible = async (id: number) => {
    const p = partners.find((pr) => pr.id === id);
    if (!p) return;
    const updated = await partnerService.update(id, { visible: !p.visible });
    setPartners((prev) => prev.map((pr) => (pr.id === id ? updated : pr)));
  };

  const handleDelete = (id: number) => setDeleteConfirm(id);

  const confirmDelete = async () => {
    if (deleteConfirm) {
      await partnerService.delete(deleteConfirm);
      setPartners((prev) => prev.filter((p) => p.id !== deleteConfirm).map((p, i) => ({ ...p, order: i + 1 })));
      setDeleteConfirm(null);
    }
  };

  const handleSave = async (data: Partial<Partner>) => {
    if (editPartner) {
      const updated = await partnerService.update(editPartner.id, data);
      setPartners((prev) => prev.map((p) => (p.id === editPartner.id ? { ...updated, order: p.order } : p)));
    } else {
      await partnerService.create(data as any);
      loadPartners();
    }
    setShowModal(false);
    setEditPartner(null);
  };

  const visibleCount = partners.filter((p) => p.visible).length;
  const hiddenCount = partners.filter((p) => !p.visible).length;

  return (
    <AdminLayout title="שותפים" subtitle="ניהול לוגואים ושותפים">
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border-2 border-emerald-100">
          <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
          <span className="text-xs font-semibold text-gray-700">{visibleCount} שותפים מוצגים</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border-2 border-gray-200">
          <div className="w-2 h-2 rounded-full bg-gray-400"></div>
          <span className="text-xs font-semibold text-gray-500">{hiddenCount} מוסתרים</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border-2 border-rose-100">
          <div className="w-4 h-4 flex items-center justify-center text-rose-400"><i className="ri-shake-hands-line text-sm"></i></div>
          <span className="text-xs font-semibold text-gray-700">{partners.length} סה״כ שותפים</span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border-2 border-amber-100 rounded-xl">
          <div className="w-4 h-4 flex items-center justify-center text-amber-500"><i className="ri-information-line text-sm"></i></div>
          <span className="text-xs text-amber-600 font-medium">השתמש בחצים לשינוי סדר התצוגה</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-white border-2 border-rose-100 rounded-xl p-1">
            {(['all', 'visible', 'hidden'] as const).map((f) => (
              <button key={f} onClick={() => setFilterVisible(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${filterVisible === f ? 'bg-rose-400 text-white' : 'text-gray-500 hover:bg-rose-50'}`}>
                {f === 'all' ? 'הכל' : f === 'visible' ? 'מוצגים' : 'מוסתרים'}
              </button>
            ))}
          </div>
          <button onClick={() => { setEditPartner(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-400 text-white text-sm font-bold hover:bg-rose-500 transition-all cursor-pointer whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base"></i></div>
            הוסף שותף
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border-2 border-rose-100 p-5 mb-5">
        <p className="text-xs font-semibold text-gray-500 mb-3">תצוגה מקדימה — כך יראו הלוגואים באתר:</p>
        <div className="flex flex-wrap items-center gap-4">
          {partners.filter((p) => p.visible).map((p) => (
            <div key={p.id} className="h-10 w-24 flex items-center justify-center rounded-xl border-2 border-rose-50 bg-rose-50/40 overflow-hidden">
              <img src={p.logo} alt={p.name} className="max-h-full max-w-full object-contain p-1" />
            </div>
          ))}
          {partners.filter((p) => p.visible).length === 0 && <p className="text-xs text-gray-400">אין שותפים מוצגים כרגע</p>}
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-4">{filtered.length} שותפים</p>

      {loading ? (
        <div className="flex justify-center py-20"><p className="text-gray-500">טוען...</p></div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filtered.map((partner) => (
            <PartnerCard
              key={partner.id}
              partner={partner as any}
              index={partners.findIndex((p) => p.id === partner.id)}
              total={partners.length}
              onDelete={handleDelete}
              onToggleVisible={handleToggleVisible}
              onMoveUp={(id) => moveItem(id, 'up')}
              onMoveDown={(id) => moveItem(id, 'down')}
              onEdit={(p) => { setEditPartner(p as any); setShowModal(true); }}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-rose-50 border-2 border-rose-100 mb-4"><i className="ri-shake-hands-line text-3xl text-rose-300"></i></div>
          <p className="text-gray-500 font-semibold">לא נמצאו שותפים</p>
          <p className="text-sm text-gray-400 mt-1">הוסף שותף חדש כדי להתחיל</p>
        </div>
      )}

      {showModal && (
        <PartnerFormModal
          partner={editPartner as any}
          onClose={() => { setShowModal(false); setEditPartner(null); }}
          onSave={handleSave as any}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center p-4" dir="rtl">
          <div className="bg-white rounded-2xl border-2 border-rose-100 p-6 w-full max-w-sm text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-red-50 border-2 border-red-100 mx-auto mb-4"><i className="ri-delete-bin-line text-2xl text-red-400"></i></div>
            <h3 className="font-bold text-gray-800 mb-2">מחיקת שותף</h3>
            <p className="text-sm text-gray-500 mb-5">האם אתה בטוח שברצונך למחוק שותף זה?</p>
            <div className="flex gap-3">
              <button onClick={confirmDelete} className="flex-1 py-2.5 rounded-xl bg-red-400 text-white text-sm font-bold hover:bg-red-500 transition-all cursor-pointer whitespace-nowrap">מחק</button>
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">ביטול</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
